import { useEffect } from 'react'
import { AppThunk } from 'src/store/store'
import { ResetPinValue, setStatus } from 'src/store/Reducer/page/loginPageSlice'
import { useLocation, useNavigate } from 'react-router-dom'
import { setDeviceAuth } from 'src/store/Reducer/socket/deviceAuthSlice'
import { setBatteryInfo } from 'src/store/Reducer/socket/batterySlice'
import { setConnection } from 'src/store/Reducer/socket/connectionSlice'
import { setCloseModal, setModalItems } from 'src/store/Reducer/common/modalSlice'
import { Cmdtypes } from 'src/constants/cmdtypes'
import { useAppDispatch } from 'src/hooks/redux/useAppDispatch'
import { Errcode } from 'src/constants/errcode'
import { commonUtils } from 'src/utils/common.utils'
import SessionStorageUtils from 'src/utils/SessionStorage.utils'
import { setErrText, setLoginStatus } from 'src/store/Reducer/common/commonSlice'
import { useTranslation } from 'react-i18next'
import { socketConnectionClosed } from 'src/store/Reducer/socket/socketSlice'
import { ResponseDataBody, ResponseDataType } from 'src/types/websocket'
import { SocketCloser, SocketSender } from 'src/api/socket'
import { eventBus } from 'src/api/eventBus'
import useMatchHook from 'src/hooks/useMatch.hook'
import { setHomeInfo } from 'src/store/Reducer/socket/homePageSlice'

const useCommonEventBus = () => {
  const { Timezone } = commonUtils
  const { pathname } = useLocation()
  const { m_login } = useMatchHook()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { updateStorageData, deleteStorageData, getStorageData } = SessionStorageUtils()
  const { t } = useTranslation()

  useEffect(() => {
    const DeviceAuthResponseListener = eventBus.subscribe<ResponseDataBody.DeviceAuth>(
      Cmdtypes.AUTH_INFO,
      handleDeviceAuthResponse,
    )
    const BatteryResponseListener = eventBus.subscribe<ResponseDataBody.Battery>(
      Cmdtypes.BATTERY_INFO,
      handleBatteryResponse,
    )
    const ConnectionResponseListener = eventBus.subscribe<ResponseDataBody.Connection>(
      Cmdtypes.CONNECT,
      (message: ResponseDataType<ResponseDataBody.Connection>) =>
        dispatch(handleConnectionResponse(message)),
    )
    const UnlockDeviceResponseListener = eventBus.subscribe<unknown>(
      Cmdtypes.UNLOCK_DEVICE,
      handleUnlockDeviceResponse,
    )
    const LogoutResponseListener = eventBus.subscribe<unknown>(
      Cmdtypes.LOGOUT,
      handleLogoutResponse,
    )
    const LockStatusResponseListener = eventBus.subscribe<unknown>(
      Cmdtypes.LOCK_STATUS,
      handleLockStatusResponse,
    )
    const LocationInfoConfirmResponseListener = eventBus.subscribe<unknown>(
      Cmdtypes.LOCATION_INFO_CONFIRM,
      (message: ResponseDataType<unknown>) => dispatch(handleLocationInfoConfirmResponse(message)),
    )
    const LoginResponseListener = eventBus.subscribe<ResponseDataBody.Login>(
      Cmdtypes.LOGIN,
      (message: ResponseDataType<ResponseDataBody.Login>) => dispatch(handleLoginResponse(message)),
    )
    const HomeInfoResponseListener = eventBus.subscribe<ResponseDataBody.HomeInfo>(
      Cmdtypes.HOME_INFO,
      handleHomeInfoResponse,
    )
    const DisConnectedToOtherLoginResponseListener = eventBus.subscribe<unknown>(
      Cmdtypes.DISCONNECTED_TO_OTHER_LOGIN,
      DisConnectedToOtherLoginResponse,
    )
    return () => {
      HomeInfoResponseListener.unsubscribe()
      DeviceAuthResponseListener.unsubscribe()
      BatteryResponseListener.unsubscribe()
      ConnectionResponseListener.unsubscribe()
      UnlockDeviceResponseListener.unsubscribe()
      LogoutResponseListener.unsubscribe()
      LockStatusResponseListener.unsubscribe()
      LocationInfoConfirmResponseListener.unsubscribe()
      LoginResponseListener.unsubscribe()
      DisConnectedToOtherLoginResponseListener.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])
  const DisConnectedToOtherLoginResponse = () => {
    deleteStorageData()
    getStorageData('errorModalMsg') === '' &&
      updateStorageData(
        'errorModalMsg',
        t('LOGOUT_TO_OTHER_LOGIN', {
          defaultValue: 'You were signed out from another device.',
        }) as string,
      )
    dispatch(setLoginStatus(false))
  }

  const handleHomeInfoResponse = (message: ResponseDataType<ResponseDataBody.HomeInfo>) => {
    const { Header, Body } = message.responseData
    if (Header.ErrCode === Errcode.SUCCESS) {
      dispatch(setHomeInfo(Body))
    } else console.error(Header)
  }

  const handleDeviceAuthResponse = (message: ResponseDataType<ResponseDataBody.DeviceAuth>) => {
    const { Header, Body } = message.responseData
    if (Header.ErrCode === Errcode.SUCCESS) {
      dispatch(setDeviceAuth(Body))
    } else console.error(Header)
  }

  const handleBatteryResponse = (message: ResponseDataType<ResponseDataBody.Battery>) => {
    const { Header, Body } = message?.responseData
    if (Header.ErrCode === Errcode.SUCCESS) {
      dispatch(setBatteryInfo(Body))
    } else console.error(Header)
  }

  const handleConnectionResponse = (
    message: ResponseDataType<ResponseDataBody.Connection>,
  ): AppThunk => {
    return async (_dispatch, getState) => {
      const { Header, Body } = message?.responseData
      const { Connection } = getState().connection
      if (Header.ErrCode === Errcode.SUCCESS) {
        if (Connection && !Body.Connection) {
          dispatch(setBatteryInfo({ BatteryInfo: null }))

          if (!m_login && getStorageData('errorModalMsg') === '') {
            dispatch(
              setModalItems({
                type: 'alert',
                text: t('MODAL.CONNECTION.LOST', {
                  defaultValue: 'The connection with the mobile phone has been lost.',
                }) as string,
              }),
            )
          }
        } else if (!Connection && Body.Connection) {
          const { text } = getState().modal
          const msg = t('MODAL.CONNECTION.LOST', {
            defaultValue: 'The connection with the mobile phone has been lost.',
          }) as string

          if (text === msg) {
            dispatch(setCloseModal())
          }
        }
        dispatch(setConnection(Body.Connection))
        dispatch(SocketSender<unknown>(Cmdtypes.AUTH_INFO))
      } else console.error(Header)
    }
  }

  const handleUnlockDeviceResponse = (message: ResponseDataType<unknown>) => {
    const { Header } = message.responseData
    if (Header.ErrCode === Errcode.SUCCESS) {
      navigate('/')
      updateStorageData(
        'errorModalMsg',
        t('MODAL.UNLOCK_DEVICE.SUCCESS', {
          defaultValue:
            "You device was just unlocked, meaning that you've been signed out of MFINDER.",
        }) as string,
      )
      dispatch(setLoginStatus(false))
      dispatch(SocketSender(Cmdtypes.LOGOUT))
    } else console.error(Header)
  }

  const handleLogoutResponse = (message: ResponseDataType<unknown>) => {
    const { Header } = message.responseData
    if (Header.ErrCode === Errcode.SUCCESS) {
      dispatch(setConnection(false))
      window.location.pathname !== '/' && navigate('/')
      dispatch(socketConnectionClosed())
    } else console.error(Header)
  }

  const handleLockStatusResponse = (message: ResponseDataType<unknown>) => {
    const { Header } = message.responseData
    updateStorageData('errorModalMsg', Header.ErrMsg as string)
    // dispatch(SocketSender(Cmdtypes.LOGOUT))
    // deleteStorageData()
    navigate('/')
  }

  const handleLocationInfoConfirmResponse = (message: ResponseDataType<unknown>): AppThunk => {
    return async (dispatch, getState) => {
      const { Header } = message.responseData
      const deviceAuth = getState().deviceAuth
      if (Header.ErrCode === Errcode.SUCCESS) {
        deviceAuth.Location && dispatch(SocketSender(Cmdtypes.LOCATION_INFO))
      } else console.error(Header)
    }
  }

  const handleLoginResponse = (message: ResponseDataType<ResponseDataBody.Login>): AppThunk => {
    return async (dispatch, getState) => {
      const { SNSType, Email, VerifyPin, pinHash } = getState().LoginPage
      const { Header, Body } = message?.responseData
      switch (Header.ErrCode) {
        case Errcode.SUCCESS:
          if (m_login) {
            updateStorageData('userInfo', {
              SNSType: SNSType,
              Email: Email,
              VerifyPin: VerifyPin,
              pinHash: pinHash,
            })
            navigate('/home')
          }
          updateStorageData('pin', { Pin: Body.Pin })
          dispatch(setBatteryInfo({ BatteryInfo: null }))
          dispatch(SocketSender(Cmdtypes.CONNECT))
          dispatch(setLoginStatus(true))
          deleteStorageData('token')
          break
        case Errcode.IS_NOT_PIN:
        case Errcode.IS_LOIN_NOT_MEMBER:
          if (m_login) {
            dispatch(setErrText(Header.ErrMsg))
          } else {
            dispatch(SocketCloser())
            updateStorageData(
              'errorModalMsg',
              t('MODAL.CONNECTION.LOST', {
                defaultValue: 'The connection with the mobile phone has been lost.',
              }) as string,
            )
            navigate('/')
          }
          break
        case Errcode.IS_NOT_SUBSCRIBE:
          if (m_login) {
            dispatch(
              setModalItems({
                type: 'alert',
                text: Header.ErrMsg,
                confirmFunc: () => window.location.reload(),
              }),
            )
          } else {
            dispatch(SocketCloser())
            updateStorageData(
              'errorModalMsg',
              t('MODAL.CONNECTION.LOST', {
                defaultValue: 'The connection with the mobile phone has been lost.',
              }) as string,
            )
            navigate('/')
          }
          break
        case Errcode.IS_NOT_DOWNLOAD_OR_NOT_LOGIN:
          if (m_login) {
            dispatch(setStatus('isNotApp'))
            dispatch(setErrText(Header.ErrMsg))
          } else {
            dispatch(SocketCloser())
            updateStorageData(
              'errorModalMsg',
              t('MODAL.CONNECTION.LOST', {
                defaultValue: 'The connection with the mobile phone has been lost.',
              }) as string,
            )
            navigate('/')
          }
          break
        case Errcode.MORE_THAN_FIVE_TIMES:
          if (m_login) {
            dispatch(setStatus('pinMoreThanFiveTimes'))
            dispatch(setErrText(Header.ErrMsg))
          } else {
            dispatch(SocketCloser())
            updateStorageData(
              'errorModalMsg',
              t('MODAL.CONNECTION.LOST', {
                defaultValue: 'The connection with the mobile phone has been lost.',
              }) as string,
            )
            navigate('/')
          }
          break
        case Errcode.FAIL_RECAPTCHA:
          dispatch(
            setModalItems({
              type: 'alert',
              text: Header.ErrMsg,
              confirmFunc: () => window.location.reload(),
            }),
          )
          break
        case Errcode.DUPLICATE_LOGIN:
          setTimeout(() => {
            dispatch(
              setModalItems({
                type: 'confirm',
                text: Header.ErrMsg,
                cancelFunc: () => window.location.reload(),
                confirmFunc: () => {
                  dispatch(
                    SocketSender(Cmdtypes.PREVIOUS_LOGOUT, {
                      SNSType: SNSType,
                      Email: Email,
                      TimeZone: Timezone,
                    }),
                  )
                },
              }),
            )
          }, 500)
          break
        default:
          console.error(Header)
          break
      }
      dispatch(ResetPinValue())
    }
  }
}

export default useCommonEventBus
